import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatDms, nudgeMinutes } from '../lib/dms';
import { BASE_TILE_LAYER, SEAMARK_TILE_LAYER } from '../lib/basemap.js';

/**
 * Interactive chart MARK EDITOR for the committee MarksManager (ra-gbrd.15,
 * expanded in ra-gbrd.30).
 *
 * Shows every club mark as a DRAGGABLE marker, distinct-styled per type, and
 * lets an admin position marks two ways:
 *   - DRAG a marker for a coarse move (writes back through `onMoveMark`).
 *   - SELECT a marker then ARROW-KEY nudge it in fine arc-minute steps — the
 *     same live write-back path as a drag (ra-gbrd.30).
 * A readout panel shows the selected mark's position in nautical degrees +
 * decimal minutes, updating live as it's dragged or nudged. Mark TYPES can be
 * shown/hidden via the legend so a busy chart can be filtered down.
 *
 * Clicking empty water calls `onAddAt` to seed a new mark at that point, and
 * selection is two-way synced with the list via `selectedId`/`onSelect`.
 *
 * Markers use an L.divIcon (an HTML chip) rather than the default Leaflet PNG
 * marker so they (a) style per type without bundling image assets and (b) avoid
 * the classic missing-marker-icon bundler footgun.
 */

const DEFAULT_CENTER = [53.3, -6.15];

/** Arrow-key nudge steps, in arc-minutes (fine → coarse). */
const NUDGE_STEPS = [
  { min: 0.001, label: "0.001'" },
  { min: 0.01, label: "0.01'" },
  { min: 0.1, label: "0.1'" },
];

/** Map mark appearance text → a fill colour (mirrors CourseMap's racing-mark hues). */
function appearanceColor(appearance) {
  const a = (appearance || '').toLowerCase();
  if (a.includes('red') || a.startsWith('r')) return '#dc2626';
  if (a.includes('green') || a.startsWith('g')) return '#16a34a';
  if (a.includes('yellow') || a.startsWith('y')) return '#d97706';
  if (a.includes('black') || a.startsWith('b')) return '#1a1a2e';
  if (a.includes('orange')) return '#ea580c';
  return '#6b7280';
}

const TYPE_STYLE = {
  racing_mark: { shape: 'circle', color: '#6b7280', radius: '50%', label: 'Racing mark' },
  shore_station: { shape: 'square', color: '#2563eb', radius: '2px', label: 'Shore station' },
  committee_boat: { shape: 'diamond', color: '#ea580c', radius: '0', label: 'Committee boat' },
};

/** Stable display order for legend + filter (matches the page's section order). */
const TYPE_ORDER = ['racing_mark', 'shore_station', 'committee_boat'];

function hasCoords(mark) {
  return (
    typeof mark.latitude === 'number' &&
    typeof mark.longitude === 'number' &&
    !(mark.latitude === 0 && mark.longitude === 0)
  );
}

/** Build a styled, optionally-highlighted divIcon for a mark. */
function buildIcon(mark, selected) {
  const type = mark.type || 'racing_mark';
  const style = TYPE_STYLE[type] || TYPE_STYLE.racing_mark;
  const fill = type === 'racing_mark' ? appearanceColor(mark.appearance) : style.color;
  const size = selected ? 20 : 14;
  const radius = style.shape === 'circle' ? '50%' : '3px';
  const rotate = style.shape === 'diamond' ? 'transform:rotate(45deg);' : '';
  const ring = selected ? 'box-shadow:0 0 0 3px rgba(37,99,235,0.55);' : '';
  const html =
    `<div style="width:${size}px;height:${size}px;background:${fill};` +
    `border:2px solid #fff;border-radius:${radius};box-sizing:border-box;` +
    `${rotate}${ring}"></div>`;
  return L.divIcon({
    html,
    className: 'marks-map-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Fit the map to all marks once, and again only when the SET of marks changes
 *  (add/remove) — never on a position change, so dragging doesn't snap the view. */
function FitToMarks({ geoMarks }) {
  const map = useMap();
  const signature = geoMarks.map((m) => m.id).sort().join('|');
  const lastSig = useRef(null);
  useEffect(() => {
    if (signature === lastSig.current) return;
    lastSig.current = signature;
    if (geoMarks.length === 0) return;
    const bounds = geoMarks.map((m) => [m.latitude, m.longitude]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [signature, geoMarks, map]);
  return null;
}

/** Pan (not zoom) to the selected mark when selection changes from the list. */
function PanToSelected({ selectedId, geoMarks }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedId) return;
    const mark = geoMarks.find((m) => m.id === selectedId);
    if (mark) map.panTo([mark.latitude, mark.longitude]);
  }, [selectedId, geoMarks, map]);
  return null;
}

/** Translate a click on empty water into a new-mark request — but only after an
 *  explicit confirm, so stray clicks while panning/navigating don't seed a mark.
 *  The click opens a small "Add a mark here?" popup; only its button calls onAddAt.
 *  Clicking elsewhere (or the popup's close) dismisses it with no effect. */
function ClickToAdd({ onAddAt }) {
  const map = useMapEvents({
    click(e) {
      if (!onAddAt) return;
      const { lat, lng } = e.latlng;
      const el = L.DomUtil.create('div', 'marks-add-popup');
      el.innerHTML =
        '<div style="margin-bottom:6px;font-size:0.85rem;">Add a mark here?</div>' +
        '<div style="font-family:monospace;font-size:0.72rem;color:#555;margin-bottom:8px;">' +
        `${formatDms(lat, 'lat')}, ${formatDms(lng, 'lng')}</div>`;
      const btn = L.DomUtil.create('button', '', el);
      btn.type = 'button';
      btn.textContent = '➕ Add mark here';
      btn.style.cssText =
        'cursor:pointer;padding:4px 10px;font-size:0.8rem;border:1px solid #2563eb;' +
        'background:#2563eb;color:#fff;border-radius:4px;';
      const popup = L.popup({ closeButton: true })
        .setLatLng(e.latlng)
        .setContent(el)
        .openOn(map);
      L.DomEvent.on(btn, 'click', () => {
        onAddAt(lat, lng);
        map.closePopup(popup);
      });
    },
  });
  return null;
}

/**
 * Arrow-key fine nudge of the selected mark. Arrow Up/Down nudge latitude,
 * Left/Right nudge longitude, each by `stepMin` arc-minutes; Shift = ×10 for a
 * coarser bump. Writes back through the same `onMoveMark` path as a drag.
 *
 * The current position is read from a ref so a rapid key-repeat always nudges
 * from the latest value, and we ignore keystrokes aimed at form fields so the
 * coordinate inputs elsewhere on the page keep their arrow keys.
 */
function NudgeKeys({ selectedId, geoMarks, stepMin, onMoveMark }) {
  const stateRef = useRef({});
  stateRef.current = { selectedId, geoMarks, stepMin, onMoveMark };

  useEffect(() => {
    function onKeyDown(e) {
      const { selectedId, geoMarks, stepMin, onMoveMark } = stateRef.current;
      if (!selectedId || !onMoveMark) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) {
        return;
      }
      let dLat = 0;
      let dLng = 0;
      switch (e.key) {
        case 'ArrowUp': dLat = 1; break;
        case 'ArrowDown': dLat = -1; break;
        case 'ArrowRight': dLng = 1; break;
        case 'ArrowLeft': dLng = -1; break;
        default: return;
      }
      const mark = geoMarks.find((m) => m.id === selectedId);
      if (!mark) return;
      e.preventDefault();
      const factor = e.shiftKey ? 10 : 1;
      const lat = dLat ? nudgeMinutes(mark.latitude, dLat * stepMin * factor) : mark.latitude;
      const lng = dLng ? nudgeMinutes(mark.longitude, dLng * stepMin * factor) : mark.longitude;
      onMoveMark(selectedId, lat, lng);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  return null;
}

export default function MarksMap({ marks, selectedId, onSelect, onMoveMark, onAddAt }) {
  // Which mark types are drawn. Defaults to all visible; toggled via the legend.
  const [visibleTypes, setVisibleTypes] = useState(() => new Set(TYPE_ORDER));
  // Arrow-key nudge granularity (arc-minutes). Default to the middle step.
  const [stepMin, setStepMin] = useState(0.01);

  const allGeoMarks = useMemo(() => marks.filter(hasCoords), [marks]);
  const geoMarks = useMemo(
    () => allGeoMarks.filter((m) => visibleTypes.has(m.type || 'racing_mark')),
    [allGeoMarks, visibleTypes],
  );

  // Per-type counts for the legend (over all geo-located marks, not the filtered set).
  const typeCounts = useMemo(() => {
    const counts = { racing_mark: 0, shore_station: 0, committee_boat: 0 };
    for (const m of allGeoMarks) {
      const t = m.type || 'racing_mark';
      if (counts[t] != null) counts[t] += 1;
    }
    return counts;
  }, [allGeoMarks]);

  const center = useMemo(() => {
    if (allGeoMarks.length > 0) {
      const lat = allGeoMarks.reduce((s, m) => s + m.latitude, 0) / allGeoMarks.length;
      const lng = allGeoMarks.reduce((s, m) => s + m.longitude, 0) / allGeoMarks.length;
      return [lat, lng];
    }
    return DEFAULT_CENTER;
  }, [allGeoMarks]);

  function toggleType(type) {
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  // The selected mark, if it's currently on the map (visible + geo-located).
  const selectedMark = selectedId ? geoMarks.find((m) => m.id === selectedId) : null;

  return (
    <div className="map-container">
      <div className="marks-map-legend">
        {TYPE_ORDER.map((type) => {
          const style = TYPE_STYLE[type];
          const on = visibleTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              className={`marks-map-legend-row marks-map-legend-toggle${on ? '' : ' is-off'}`}
              onClick={() => toggleType(type)}
              title={on ? `Hide ${style.label.toLowerCase()}s` : `Show ${style.label.toLowerCase()}s`}
              aria-pressed={on}
            >
              <span
                className="marks-map-legend-swatch"
                style={{ background: style.color, borderRadius: style.radius, transform: style.shape === 'diamond' ? 'rotate(45deg)' : 'none' }}
              />
              {style.label}
              <span className="marks-map-legend-count">{typeCounts[type]}</span>
            </button>
          );
        })}
      </div>

      {selectedMark ? (
        <div className="marks-map-readout">
          <div className="marks-map-readout-name"><strong>{selectedMark.id}</strong> {selectedMark.name || ''}</div>
          <div className="marks-map-readout-coord">{formatDms(selectedMark.latitude, 'lat')}</div>
          <div className="marks-map-readout-coord">{formatDms(selectedMark.longitude, 'lng')}</div>
          <div className="marks-map-readout-step">
            <span>Nudge step:</span>
            {NUDGE_STEPS.map((s) => (
              <button
                key={s.min}
                type="button"
                className={`marks-map-step-btn${stepMin === s.min ? ' is-active' : ''}`}
                onClick={() => setStepMin(s.min)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="marks-map-readout-hint">Arrow keys nudge · Shift = ×10</div>
        </div>
      ) : null}

      <div className="marks-map-hint">Select a mark to nudge with arrow keys · drag to reposition · click empty water, then confirm, to add</div>
      <MapContainer center={center} zoom={13} className="course-map" keyboard={false}>
        <TileLayer {...BASE_TILE_LAYER} />
        <TileLayer {...SEAMARK_TILE_LAYER} />
        <FitToMarks geoMarks={geoMarks} />
        <PanToSelected selectedId={selectedId} geoMarks={geoMarks} />
        <ClickToAdd onAddAt={onAddAt} />
        <NudgeKeys selectedId={selectedId} geoMarks={geoMarks} stepMin={stepMin} onMoveMark={onMoveMark} />
        {geoMarks.map((m) => (
          <Marker
            key={m.id}
            position={[m.latitude, m.longitude]}
            draggable
            icon={buildIcon(m, m.id === selectedId)}
            eventHandlers={{
              click: () => onSelect && onSelect(m.id),
              dragend: (e) => {
                const { lat, lng } = e.target.getLatLng();
                if (onMoveMark) onMoveMark(m.id, lat, lng);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -10]}>
              <strong>{m.id}</strong> {m.name || ''}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
